import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getTenantProducts, updateTenantProducts } from "../api/capabilityApi";
import { apiErrorMessage } from "../utils/capabilities";

export const PRODUCT_CHOICES = [
  { value: "iot", products: ["iot"] },
  { value: "cra", products: ["cra"] },
  { value: "both", products: ["cra", "iot"] },
];
const choiceOf = (products) => (products.includes("iot") && products.includes("cra") ? "both" : products.includes("cra") ? "cra" : "iot");

// Superadmin only (shown in TenantList's capabilities tab). Picks what the tenant sees in the menu; "cra" also switches
// the security.sbom capability on, so the two can never disagree.
export default function TenantProducts({ tenantId, accessToken, onSaved }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(null);
  const [value, setValue] = useState("iot");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSaved(null);
    getTenantProducts(accessToken, tenantId)
      .then((data) => { if (!cancelled) { const c = choiceOf(data.products || []); setSaved(c); setValue(c); } })
      .catch((err) => { if (!cancelled) { toast.error(apiErrorMessage(err, t("admin.products.loadFailed"))); setSaved("iot"); } });
    return () => { cancelled = true; };
  }, [accessToken, tenantId, t]);

  if (saved === null) return <CircularProgress size={22} sx={{ color: "var(--text-muted)" }} />;

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await updateTenantProducts(accessToken, tenantId, PRODUCT_CHOICES.find((c) => c.value === value).products);
      const c = choiceOf(data.products || []);
      setSaved(c);
      setValue(c);
      toast.success(t("admin.products.saved"));
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, t("admin.products.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px", p: 3, maxWidth: 560, mb: 2 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t("admin.products.title")}</Typography>
      <Typography variant="caption" sx={{ color: "var(--text-muted)", display: "block", mb: 1 }}>
        {t("admin.products.help")}
      </Typography>
      <RadioGroup value={value} onChange={(e) => setValue(e.target.value)} aria-label={t("admin.products.title")}>
        {PRODUCT_CHOICES.map((c) => (
          <FormControlLabel key={c.value} value={c.value} control={<Radio size="small" disabled={saving} />} label={t(`admin.products.${c.value}`)} />
        ))}
      </RadioGroup>
      <Button size="small" variant="contained" onClick={handleSave} disabled={saving || value === saved} sx={{ mt: 1, backgroundColor: "var(--btn-primary)" }}>
        {saving ? t("admin.products.saving") : t("admin.products.save")}
      </Button>
    </Box>
  );
}
